import { useMemo } from 'react';

import { competencyTransform } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import onlineReputationBanner from '@backrs_ui/grow-ui/src/assets/images/online-reputation-guide/online-reputation-banner.png';
import {
  GuideAccordionList,
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { SxProps, Theme } from '@mui/material';

import { StepSection, WhyItMattersSection } from './components';
import { useAgentContext } from './OnlineReputationGuidePageContainer.agentContext';
import { onlineReputationContent } from '../content';

const SECTION_IDS = {
  overview: 'online-reputation-section-overview',
  tools: 'online-reputation-section-tools',
  steps: 'online-reputation-section-steps',
  whyItMatters: 'online-reputation-section-why-it-matters',
} as const;

const ONLINE_REPUTATION_GUIDE_TOOL_ID = 'online_reputation_guide';

const ONLINE_REPUTATION_TOOL_IDS: readonly StickerCardId[] = [];

const ONLINE_REPUTATION_COMPETENCY_SLUG = 'careers-social-media';

const stepDetailsSx: SxProps<Theme> = ({ backrs }) => ({
  pb: backrs.spacing['spacing-4xl'],
});

const ONLINE_REPUTATION_NUGGET_SLUGS: readonly string[] = [
  'social-media-job-search-dos-and-donts',
  '6-reasons-you-should-clean-up-your-social-media-pages-before-college',
  'why-should-i-clean-up-my-social-media',
  'the-right-way-to-use-social-media-',
  'how-to-be-classy-on-social-media-',
  'most-common-mistakes-when-using-social-media-for-career-development',
];

export const OnlineReputationGuidePageContainer = () => {
  useAgentContext();

  const { data: resourceNuggets } = useGetContentNuggetsBySlugsQuery(
    ONLINE_REPUTATION_NUGGET_SLUGS,
  );
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const competencyNugget = useMemo(() => {
    const competency = contentCompetencies.find(
      (c) => c.slug === ONLINE_REPUTATION_COMPETENCY_SLUG,
    );

    return competency ? competencyTransform(competency) : undefined;
  }, [contentCompetencies]);

  const orderedNuggets = useMemo(() => {
    if (!resourceNuggets) return [];

    const bySlug = new Map(resourceNuggets.map((n) => [n.slug, n]));

    return ONLINE_REPUTATION_NUGGET_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (n): n is NonNullable<typeof n> => Boolean(n),
    );
  }, [resourceNuggets]);

  const extraNuggets = useMemo(
    () => (competencyNugget ? [competencyNugget, ...orderedNuggets] : orderedNuggets),
    [competencyNugget, orderedNuggets],
  );

  const stepItems = onlineReputationContent.steps.map((step) => ({
    id: step.id,
    title: step.title,
    chipLabel: `${appStrings.modules.onlineReputationGuide.steps.stepLabel} ${step.number}`,
    details: <StepSection content={step} />,
  }));

  const sections = [
    {
      id: SECTION_IDS.steps,
      title: onlineReputationContent.stepsSection.title,
      subtitle: onlineReputationContent.stepsSection.subtitle,
      rightSlot: (
        <LubavButton
          id="online-reputation-guide-steps-lubav-btn"
          label="Apply this to me"
          prompt="Based on what you know about me, help me apply these eight steps for protecting my online reputation to my own social media accounts."
          allowTools={false}
        />
      ),
      content: (
        <GuideAccordionList
          idPrefix="online-reputation-guide-step"
          items={stepItems}
          detailsSx={stepDetailsSx}
        />
      ),
    },
    {
      id: SECTION_IDS.tools,
      title: onlineReputationContent.tools.title,
      subtitle: onlineReputationContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="online-reputation-guide-tools-lubav-btn"
          label="Where should I start?"
          prompt="Which of the tools or resources in this section should I start with to protect my online reputation?"
          allowTools={false}
        />
      ),
      content: (
        <GuideToolsSection toolIds={ONLINE_REPUTATION_TOOL_IDS} extraNuggets={extraNuggets} />
      ),
    },
    {
      id: SECTION_IDS.whyItMatters,
      title: onlineReputationContent.whyItMatters.title,
      subtitle: onlineReputationContent.whyItMatters.subtitle,
      rightSlot: (
        <LubavButton
          id="online-reputation-guide-why-lubav-btn"
          label="Discuss with Lubav"
          prompt={`I just read the "${onlineReputationContent.whyItMatters.title}" section of the online reputation guide. Based on what you know about me, how could my online presence help or hurt my college and career goals?`}
          allowTools={false}
        />
      ),
      content: <WhyItMattersSection content={onlineReputationContent.whyItMatters} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={ONLINE_REPUTATION_GUIDE_TOOL_ID}
      banner={{
        src: onlineReputationBanner,
        alt: 'Protecting Your Online Reputation',
      }}
    >
      <GuideSections
        header={onlineReputationContent.header}
        overview={{ id: SECTION_IDS.overview }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
